import { Bucket, Creative, ICreativeRequest } from '@kaizenplatform/creative-search-entity';
import OrganizationTag from '@kaizenplatform/creative-search-entity/dist/entity/FacetParts/OrganizationTag';
import { History } from 'history';
import Store from 'repatch';

import State from '../core/State';
import CreativeIndexForDirectorWeb from '../entity/CreativeForDirectorWeb';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import AdSearchWebApi from '../service/persistent/AdSearchWebApi';
import { failFetchAdVideo, setData, setSelectedVideo, startFetchAdVideo, successFetchAdVideo } from '../thunk/adSearch';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import { sortFunc } from '../utility/creativeSort';

export interface AdSearchInput {
  query: string | null;
  duration: string | null;
  aspectRatio: string | null;
  industry: string | null;
  placement: string | null;
  offset: number;
  organizationTag: string[];
}

class AdShowCaseUsecase {
  private readonly store: Store<State>;
  private adSearchWebApi: AdSearchWebApi;
  private errorPresenter: ErrorPresenter;
  private history: History;

  constructor({
    store,
    adSearchWebApi,
    errorPresenter,
    history,
  }: {
    store: Store<State>;
    adSearchWebApi: AdSearchWebApi;
    errorPresenter: ErrorPresenter;
    history: History;
  }) {
    this.store = store;
    this.adSearchWebApi = adSearchWebApi;
    this.errorPresenter = errorPresenter;
    this.history = history;
  }

  /**
   *
   * 現在の組織をセットする関数
   * @param organization 現在ログイン中の組織.
   */
  public setLastAccessedOrg(organization: OrganizationDetailFragment | undefined) {
    organization && this.store.dispatch(setLastAccessedOrganization(organization));
  }

  /**
   *
   * 選択したクリエイティブページへ遷移する関数
   * @param card 選択されたCretiveデータ.
   */
  public showDetail(card: Creative) {
    this.store.dispatch(setSelectedVideo([card]));
    this.history.push(`/search/${card.id}`);
  }

  /**
   * ビデオを取得する処理。処理に成功したらstoreにデータをセットする。失敗したらエラートーストを表示させる。
   */
  public getAdVideo = async (request: AdSearchInput): Promise<void> => {
    this.store.dispatch(startFetchAdVideo());

    let data: CreativeIndexForDirectorWeb;
    try {
      const req = toApiRequest(request);
      data = await this.adSearchWebApi.getCreatives(req);
    } catch (err) {
      console.error(err);
      this.store.dispatch(failFetchAdVideo(err));
      this.errorPresenter.showAsFatal(err);
      return;
    }
    // 取得したデータをソートする
    const sortedData: CreativeIndexForDirectorWeb = {
      ...data,
      facets: {
        industry: {
          buckets: data.facets.industry.buckets,
        },
        placement: {
          buckets: data.facets.placement.buckets.sort(sortFunc),
        },
        aspectRatio: {
          buckets: data.facets.aspectRatio.buckets,
        },
        composition: {
          buckets: data.facets.composition.buckets,
        },
        organizationTag: {
          buckets: data.facets.organizationTag.buckets,
        },
      },
    };

    // 取得したデータをstoreにsetする(loader止める)
    this.store.dispatch(successFetchAdVideo(sortedData));
  };

  public getMoreAdVideo = async (request: AdSearchInput): Promise<void> => {
    this.store.dispatch(startFetchAdVideo());
    let data: CreativeIndexForDirectorWeb;
    try {
      const req = toApiRequest(request);
      data = await this.adSearchWebApi.getCreatives(req);
    } catch (err) {
      console.error(err);
      this.store.dispatch(failFetchAdVideo(err));
      this.errorPresenter.showAsNonFatal(err);
      return;
    }
    // 取得したデータをソートする
    const sortedData: CreativeIndexForDirectorWeb = {
      ...data,
      facets: {
        industry: {
          buckets: data.facets.industry.buckets,
        },
        placement: {
          buckets: data.facets.placement.buckets.sort(sortFunc),
        },
        aspectRatio: {
          buckets: data.facets.aspectRatio.buckets,
        },
        composition: {
          buckets: data.facets.composition.buckets,
        },
        organizationTag: {
          buckets: data.facets.composition.buckets,
        },
      },
    };
    const existedData = this.store.getState().adSearch.data;
    let addedData: CreativeIndexForDirectorWeb;
    if (existedData) {
      addedData = CreativeIndexForDirectorWeb.fromJSON({
        count: existedData.count,
        facets: existedData.facets,
        creatives: [...existedData.creatives, ...sortedData.creatives],
      });
    } else {
      // 初回ロードしていないので、取ってきたデータを初回データとして保管する
      addedData = CreativeIndexForDirectorWeb.fromJSON(data);
    }

    // 取得したデータをstoreにsetする(loader止める)
    this.store.dispatch(successFetchAdVideo(addedData));
  };

  /**
   * 指定されたidのcreativeを取得して、選択中クリエイティブとしてstoreに保存する.
   */
  public setSelectedCardDataByIds = async (cardIds: string[]): Promise<Creative[] | void> => {
    let data: Creative[];
    try {
      data = await this.adSearchWebApi.getCreativesByIds(cardIds);
    } catch (err) {
      console.error(err);
      this.store.dispatch(failFetchAdVideo(err));
      this.errorPresenter.showAsFatal(err);
      return;
    }
    return data;
  };

  /**
   * 指定されたcreativeを選択中クリエイティブとしてstoreに保存する.
   */
  public setSelectedCardData = async (cards: Creative[]): Promise<void> => {
    this.store.dispatch(setSelectedVideo(cards));
  };

  /**
   * 新しいプロジェクトを作るページへと遷移
   */
  public moveToCreateNewProject = () => {
    this.history.push(`/direction_types`);
  };

  /**
   * searchへ戻る
   */
  public moveToSearch = () => {
    this.history.push(`/search`);
  };

  /**
   * tagを追加する. 追加時、サーバーに処理させる前にUIを反映させる。
   * @param cid creative id
   * @param name tagの名前
   */
  public addTagToCreative = async (creative: Creative, name: string): Promise<void> => {
    const data = this.store.getState().adSearch.data;
    if (!data) throw new Error('unsetted data');
    const creatives = [];
    for (let i = 0; i < data.creatives.length; i++) {
      const targetCreative = data.creatives[i];
      if (targetCreative.id === creative.id) {
        const addedTagCreative = { ...targetCreative, organizationTag: [name, ...targetCreative.organizationTag] };
        creatives.push(addedTagCreative);
      } else {
        creatives.push(targetCreative);
      }
    }

    // allFacetsのUIにtagを追加する. ユニークでなければ追加しなくて良い
    const addedOrganizationTags = data.facets.organizationTag.buckets.map((bucket) => bucket.value).includes(name)
      ? data.facets.organizationTag.buckets
      : [...data.facets.organizationTag.buckets, Bucket.fromJSON({ value: name, count: 1 })]; // 既存タグにない場合にタグ追加、そのときcountは1(初めて作られたものだから)にセットする.

    const addedData = CreativeIndexForDirectorWeb.fromJSON({
      ...data,
      facets: { ...data.facets, organizationTag: OrganizationTag.fromJSON({ buckets: addedOrganizationTags }) },
      creatives,
    });

    this.store.dispatch(setData(addedData));
    try {
      await this.adSearchWebApi.addTagToCreative(creative.id, name, creative.organizationId);
    } catch (err) {
      console.error(err);
      this.errorPresenter.showAsNonFatal(err);
      return;
    }
  };

  /**
   * tagを削除する. 削除時、サーバーに処理させる前にUIを反映させる。
   * @param cid creative id
   * @param name tagの名前
   */
  public deleteTagToCreative = async (creative: Creative, name: string): Promise<void> => {
    // API処理より先に投棄的に実行する
    const data = this.store.getState().adSearch.data;
    if (!data) throw new Error('unsetted data');

    // crativeのUIからtagを消す
    const creatives = [];
    for (let i = 0; i < data.creatives.length; i++) {
      const targetCreative = data.creatives[i];
      if (targetCreative.id === creative.id) {
        const deleted = {
          ...targetCreative,
          organizationTag: targetCreative.organizationTag.filter((t) => t !== name),
        };
        creatives.push(deleted);
      } else {
        creatives.push(targetCreative);
      }
    }

    // allFacetsのUIからtagを消す. 現時点で残り件数が2つあればタグ一覧から消さなくても良い
    const filteredOrganizationTags = data.facets.organizationTag.buckets
      // TODO: serverがcountを0で返してるからfilterに引っかからない. なので消えてくれない.
      .filter((bucket) => bucket.value !== name || bucket.count > 1)
      .map((bucket) => {
        return bucket.value === name ? Bucket.fromJSON({ ...bucket, count: bucket.count - 1 }) : bucket;
      });

    const filteredData = CreativeIndexForDirectorWeb.fromJSON({
      ...data,
      facets: { ...data.facets, organizationTag: OrganizationTag.fromJSON({ buckets: filteredOrganizationTags }) },
      creatives,
    });

    this.store.dispatch(setData(filteredData));
    try {
      await this.adSearchWebApi.deleteTagfromCreative(creative.id, name, creative.organizationId);
    } catch (err) {
      console.error(err);
      this.errorPresenter.showAsNonFatal(err);
      return;
    }
  };

  /**
   * ファボもしくはファボ解除する。
   */
  public setFavUnfav = async (cid: string, shouldFavorite: 0 | 1): Promise<void> => {
    const data = this.store.getState().adSearch.data;
    if (!data) throw new Error('unsetted data');

    const creatives = [];
    for (let i = 0; i < data.creatives.length; i++) {
      const targetCreative = data.creatives[i];
      if (targetCreative.id === cid) {
        const toggledCreative = { ...targetCreative, favorite: shouldFavorite };
        creatives.push(toggledCreative);
      } else {
        creatives.push(targetCreative);
      }
    }
    const filteredData = {
      ...data,
      creatives,
    };
    this.store.dispatch(setData(filteredData));
    if (shouldFavorite) {
      try {
        await this.adSearchWebApi.creativeFav(cid);
      } catch (err) {
        console.error(err);
        this.errorPresenter.showAsNonFatal(err);
        return;
      }
    } else {
      try {
        await this.adSearchWebApi.creativeUnfav(cid);
      } catch (err) {
        console.error(err);
        this.errorPresenter.showAsNonFatal(err);
        return;
      }
    }
  };
}

/**
 * UIが持っているrequest情報で、APIが求めるrequestオブジェクトに変換する.
 * - 配列は文字列の','結合に変換する
 * - valueがnullなものは, (key, value)共にAPIリクエストに含めない
 */
export const toApiRequest = (uiRequest: AdSearchInput): ICreativeRequest => {
  const apiRequest = (Object.keys(uiRequest) as (keyof typeof uiRequest)[]).reduce((acc: ICreativeRequest, cur) => {
    const value = uiRequest[cur];
    if (value == null) return acc;
    if (cur === 'organizationTag') {
      if (uiRequest['organizationTag'].length > 0) {
        acc['organizationTag'] = uiRequest['organizationTag'].join(',');
      }
    } else {
      acc[cur] = value as any;
    }
    return acc;
  }, {});
  apiRequest.scope = '1,2';
  return apiRequest;
};

export default AdShowCaseUsecase;
